import { useCallback } from 'react'
import { toast as sonnerToast } from 'sonner'

export type ToastType = 'success' | 'error' | 'info'

/**
 * Tiny wrapper over sonner so consumers can keep the legacy
 * `addToast(type, message, duration)` call signature while toasts now
 * actually render (the previous Toast/ToastContainer pair was wired but
 * the container was never mounted, so calls were silent).
 *
 * The visual styling lives in `<Toaster>` in components/Sonner.tsx, which
 * is mounted globally in App.tsx. T3 tokens flip with theme automatically.
 *
 * For new code, prefer `import { toast } from 'sonner'` directly — it's
 * smaller and the surface is well-documented. This hook stays for API
 * compatibility with surfaces that already wired it (login OTP errors,
 * onboarding presence checks, etc.).
 */
export const useToast = () => {
  const addToast = useCallback(
    (type: ToastType, message: string, duration?: number): string | number => {
      const opts = duration !== undefined ? { duration } : undefined
      switch (type) {
        case 'success':
          return sonnerToast.success(message, opts)
        case 'error':
          return sonnerToast.error(message, opts)
        case 'info':
        default:
          return sonnerToast.info(message, opts)
      }
    },
    []
  )

  const removeToast = useCallback((id: string | number) => {
    sonnerToast.dismiss(id)
  }, [])

  const clearAllToasts = useCallback(() => {
    sonnerToast.dismiss()
  }, [])

  const showSuccess = useCallback(
    (message: string, duration?: number) => addToast('success', message, duration),
    [addToast]
  )

  const showError = useCallback(
    (message: string, duration?: number) => addToast('error', message, duration),
    [addToast]
  )

  const showInfo = useCallback(
    (message: string, duration?: number) => addToast('info', message, duration),
    [addToast]
  )

  // `toasts` returned for legacy API compat — sonner manages its own
  // queue, so this is always empty. Code that reads `toasts.length` to
  // conditionally render a container should switch to <Toaster /> globally
  // (already mounted in App.tsx) and stop relying on this field.
  return {
    toasts: [] as Array<{ id: string; type: ToastType; message: string }>,
    addToast,
    removeToast,
    clearAllToasts,
    showSuccess,
    showError,
    showInfo,
  }
}
